import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ProductPicker, type PickedProduct } from "../components/ProductPicker";
import { PageHeader, Field, NoAccess, ErrorNote, Spinner } from "../components";
import { useDoc } from "../hooks/useDoc";
import { useCallableAction, callableErrorMessage } from "../hooks/useCallableAction";
import { callable } from "../lib/firebase";
import { useCan, useSession } from "../lib/auth";
import { tl } from "../lib/format";
import { formatKurusInput, parseKurus, sanitizeDecimalKeystrokes } from "../lib/decimal";
import { CARGO_PROVIDERS } from "../lib/cargo";

/**
 * MANUEL SİPARİŞ GİRİŞİ — Shopify'dan GELMEYEN sipariş.
 *
 * NİÇİN VAR: ofis fotoğraf çekimi, numune ve iç kullanım için sipariş açmak
 * zorunda ("Çekim için sipariş ekleyecektik ama manuel sipariş giremiyoruz").
 * Bu kayıtların Shopify'da karşılığı YOKTUR ve olmamalıdır: mağazada satılmamış
 * bir ürün için Shopify'da sipariş açmak stoğu, ciroyu ve müşteri kayıtlarını
 * kirletir. Sipariş yalnızca Balder'de yaşar.
 *
 * ── EKRAN HİÇBİR ŞEY YAZMAZ ────────────────────────────────────────────────
 * Form Firestore'a DOKUNMAZ; tek yaptığı `createManualOrder` callable'ını
 * çağırmaktır. Sebep `StockOrders` ekranında yaşanan arızadır: form koleksiyona
 * elle doküman ekliyordu ve sunucunun beklediğinden BAŞKA alan adları yazıyordu
 * — `recipeOwnerId` doğmuyor, kesim listesi boş çıkıyor, hata veren bir şey
 * olmuyordu. Alan sözleşmesi tek kaynaktadır (`orders/contract.ts`) ve ona
 * yalnızca sunucu dokunur.
 *
 * ── ÜRÜN KATALOGDAN SEÇİLİR, FİYATI DA ONUNLA GELİR ────────────────────────
 * Serbest metin "Ürün ID" kutusu YOK. Kimse Shopify varyant kimliğini ezbere
 * bilmiyor; yanlış kimlik girilen kalem hiçbir ürüne bağlanmıyor, reçete
 * bulunamıyor, maliyet ve kesim listesi sessizce boş çıkıyordu.
 *
 * ★ ÇÖZÜLEN ARIZA: seçilen ürünün fiyatı gelmiyor, "Birim fiyat (KDV dahil)"
 * kutusu boş kalıyor ve satır toplamı ₺0,00 görünüyordu. Sebep `ProductPicker`
 * bileşeninin ürün dokümanından FİYAT ALANINI HİÇ OKUMAMASIYDI (`name`, `sku`,
 * `imageUrl`, `stockPolicy` okunuyordu; `price` okunmuyordu). Veri yerindeydi —
 * ölçüm: 516/516 üründe `price` dolu, tam sayı ve KURUŞ birimli. Firestore
 * sorulmayan alanı vermez ve hata da vermez; bu projenin klasik sessiz arızası.
 * Fiyat artık seçiciyle birlikte gelir ve buraya yazılır — ELLE DEĞİŞTİRİLEBİLİR
 * kalır (indirimli numune, bedelsiz çekim siparişi meşrudur).
 *
 * ── SEBEP EKRANI DEĞİŞTİRİR ────────────────────────────────────────────────
 * "Fotoğraf çekimi" / "Numune" / "İç kullanım" seçilirse alıcı Demontha'nın
 * KENDİSİDİR: ad, e-posta, adres, il/ilçe, posta kodu SORULMAZ; bilgiler
 * `settings/company` dokümanından gelir ve burada yalnızca ÖZET olarak
 * gösterilir. Yerine iki alan ZORUNLU olur: siparişi açan yetkilinin adı ve
 * siparişin niçin açıldığı. "Diğer" seçilirse eski davranış aynen sürer.
 *
 * ⚠ Bu ekrandaki doğrulama YALNIZCA kullanıcı deneyimidir. Gerçek kural
 * sunucudadır (`orders/manual.ts:manualInternalFieldErrors` + iç siparişte
 * alıcı bilgisinin `settings/company`den YENİDEN yazılması). Metinler bilerek
 * aynı tutuldu ki kullanıcı iki ayrı dilde iki farklı hata okumasın.
 */

/* ══════════════════════════════════════════════════════════════════════════
 * 1. SEBEP
 * ═══════════════════════════════════════════════════════════════════════ */

/** Sunucudaki `MANUAL_ORDER_REASONS` ile AYNI kodlar — sıra ekran sırasıdır. */
const REASONS = [
  { id: "photo", label: "Fotoğraf çekimi", hint: "Katalog / sosyal medya çekimi" },
  { id: "sample", label: "Numune", hint: "Showroom, bayi ya da müşteri numunesi" },
  { id: "internal", label: "İç kullanım", hint: "Ofis, atölye ya da personel kullanımı" },
  // ★ 2026-08-31: "Stok Siparişi" kendi menü sayfasıydı, artık burada bir sebep.
  { id: "stock", label: "Stok siparişi", hint: "Satış için değil, stoğa üretim" },
  { id: "other", label: "Diğer", hint: "Telefonla satış vb. — alıcı bilgisi sorulur" },
] as const;

type ReasonId = (typeof REASONS)[number]["id"];

/**
 * Alıcısı Demontha olan sebepler — sunucudaki `INTERNAL_MANUAL_REASONS` ile
 * AYNI liste. İki yerde durması bilinçli: sunucu otorite, burası yalnızca
 * hangi kutuların çizileceğine karar veriyor. Ayrışırsa sonuç "ekranda sorulan
 * ama sunucunun görmezden geldiği kutu" olur; bunu önlemek için liste tek
 * satırda ve yorumla işaretli.
 */
const INTERNAL_REASONS: readonly ReasonId[] = ["photo", "sample", "internal", "stock"];

function isInternalReason(r: ReasonId): boolean {
  return INTERNAL_REASONS.includes(r);
}

/* ══════════════════════════════════════════════════════════════════════════
 * 2. ŞİRKET BİLGİSİ
 * ═══════════════════════════════════════════════════════════════════════ */

/**
 * `settings/company` — iç siparişin alıcı bilgisi.
 *
 * NİÇİN KODA GÖMÜLMEDİ: şirket adresi/telefonu değiştiği gün yeni bir dağıtım
 * gerekirdi. Doküman Ayarlar → "Şirket Bilgileri" bölümünden düzenlenir; okuma
 * yetkisi `isActive()` (Rules §12) — maliyet sırrı değil, ofisin görmesi gereken
 * bir bilgi.
 */
interface CompanyDoc {
  name?: string;
  email?: string;
  phone?: string;
  address1?: string;
  city?: string;
  province?: string;
  zip?: string;
  country?: string;
}

/* ══════════════════════════════════════════════════════════════════════════
 * 3. KALEM
 * ═══════════════════════════════════════════════════════════════════════ */

interface Line {
  /** Liste anahtarı — aynı ürün iki kez eklenebilir, `productId` anahtar olamaz. */
  key: string;
  product: PickedProduct;
  /** Metin taşınır; sayıya çevrim kaydetme anında yapılır (ara değerler bozulmasın). */
  quantity: string;
  /** TL metni. Kuruşa çevrim `parseKurus()` ile — sunucudaki `toKurus` ile aynı. */
  price: string;
  /**
   * Katalog fiyatı (KURUŞ) — "fiyat değişti mi" rozetinin girdisi.
   * Kullanıcı elle değiştirdiğinde ekran bunu AÇIKÇA söyler; sessizce farklı
   * bir fiyatla sipariş açmak, aylar sonra "niye bu tutar" sorusunu doğurur.
   */
  catalogPriceKurus: number;
  /**
   * ★ RENK / SEÇENEK. Kumaşlı ürünün renkleri katalogda değil Shopify'dadır;
   * ürün eklenince sunucudan okunur. `null` = henüz yükleniyor, `[]` = ürünün
   * seçeneği yok (renk sorulmaz). Seçenek varsa seçim ZORUNLUDUR — atölye hangi
   * kumaşla üreteceğini kalemden okur.
   */
  variants: VariantOption[] | null;
  variantId: string;
  variantError: string | null;
}

interface VariantOption {
  id: string;
  title: string;
  sku: string;
}

/** Kalemin satır toplamı (KURUŞ). Eksik/bozuk girdi 0 sayılır — önizleme patlamaz. */
function lineTotalKurus(l: Line): number {
  const qty = Math.trunc(Number(l.quantity));
  const unit = parseKurus(l.price);
  if (!Number.isFinite(qty) || qty < 1 || unit === null || unit < 0) return 0;
  return unit * qty;
}

/* ══════════════════════════════════════════════════════════════════════════
 * 4. SAYFA
 * ═══════════════════════════════════════════════════════════════════════ */

interface CreateManualOrderResult {
  orderId: string;
  orderName: string;
  countsAsRevenue: boolean;
  totals: { total: number; subtotal: number; tax: number };
}

export default function ManualOrder() {
  const can = useCan();
  const session = useSession();
  const navigate = useNavigate();

  const [reason, setReason] = useState<ReasonId>("photo");

  // ── Alıcı (yalnızca "Diğer") ──────────────────────────────────────────────
  const [customerName, setCustomerName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address1, setAddress1] = useState("");
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [zip, setZip] = useState("");

  // ── İç sipariş (çekim / numune / iç kullanım) ─────────────────────────────
  const [requestedBy, setRequestedBy] = useState("");
  const [reasonNote, setReasonNote] = useState("");

  const [cargoCompany, setCargoCompany] = useState("");
  const [note, setNote] = useState("");
  const [countsAsRevenue, setCountsAsRevenue] = useState(false);
  const [lines, setLines] = useState<Line[]>([]);
  const [showErrors, setShowErrors] = useState(false);

  const internal = isInternalReason(reason);

  const create = useCallableAction<Record<string, unknown>, CreateManualOrderResult>(
    "createManualOrder",
  );

  /**
   * Şirket bilgisi — iç siparişin alıcısı. Doküman yoksa `notFound` döner ve
   * ekran bunu AÇIKÇA söyler; sessizce boş adresle sipariş açmak, kargo
   * etiketine boş adres basılmasına kadar fark edilmezdi.
   */
  const {
    data: company,
    loading: companyLoading,
    notFound: companyMissing,
    error: companyError,
  } = useDoc<CompanyDoc>("settings/company");

  const companyName = company?.name?.trim() || "Demontha";

  /**
   * Oturum sahibinin adı VARSAYILAN gelir ama kilitli değildir: siparişi çoğu
   * zaman bilgisayarın başındaki kişi başkası adına açıyor. Yalnızca kullanıcı
   * henüz bir şey yazmadıysa doldurulur — yazdıysa üstüne yazmak, kullanıcının
   * emeğini silmek olurdu.
   */
  useEffect(() => {
    const ad = session?.displayName?.trim();
    if (ad) setRequestedBy((prev) => (prev ? prev : ad));
  }, [session?.displayName]);

  /**
   * KDV oranı ayardan gelir, koda GÖMÜLMEZ.
   *
   * `settings/finance` okuması `costView` ister. Bu ekran zaten boss+office'e
   * açık ve ikisinde de `costView` var; yine de okuma başarısız olursa ekran
   * ÇALIŞMAYA DEVAM EDER: aşağıdaki hesapta görüleceği gibi KDV toplamı
   * DEĞİŞTİRMEZ, yalnızca kırılımı değiştirir. Bu yüzden oran yoksa kırılım
   * gizlenir, genel toplam yine doğrudur.
   */
  const { data: finance, error: financeError } = useDoc<{ kdv?: number }>("settings/finance");
  const kdvRate = typeof finance?.kdv === "number" ? finance.kdv : null;

  /**
   * ── CANLI TOPLAM ────────────────────────────────────────────────────────
   * ★ BU BİR ÖNİZLEMEDİR, OTORİTE DEĞİLDİR. Yazılan tutarları sunucu
   * `orders/manual.ts:computeManualTotals` ile YENİDEN hesaplar ve doküman ona
   * göre yazılır. Buradaki hesap yalnızca ofis kaydetmeden önce rakamı görsün
   * diye var; ikisinin ayrışmasını birim testi sunucu tarafında kilitler.
   *
   * ★ KDV FİYATIN İÇİNDEN AYRIŞIR, ÜSTÜNE EKLENMEZ. Türkiye perakendesinde
   * ekranda yazılan fiyat KDV DAHİLDİR ve Shopify de `total_price`ı KDV dahil
   * gönderir. Üstüne eklenseydi aynı ürünün manuel siparişi Shopify
   * siparişinden %10 pahalı görünürdü.
   */
  const totals = useMemo(() => {
    const total = lines.reduce((s, l) => s + lineTotalKurus(l), 0);
    if (kdvRate === null || kdvRate <= 0) return { total, net: total, tax: null as number | null };
    // Net yuvarlanır, KDV FARKTAN bulunur: net + KDV her zaman TAM toplamı verir
    // (iki ayrı yuvarlama 1 kuruşluk açık bırakırdı). Sunucudaki hesabın aynısı.
    const net = Math.round(total / (1 + kdvRate / 100));
    return { total, net, tax: total - net };
  }, [lines, kdvRate]);

  /* ── Doğrulama ─────────────────────────────────────────────────────────────
   * Kural sebebe göre DEĞİŞİR. Metinler sunucudaki
   * `manualInternalFieldErrors` ile aynı tutuldu: kullanıcı aynı eksikliği iki
   * farklı cümleyle okumasın.
   */
  const reasonLabel = REASONS.find((r) => r.id === reason)?.label ?? "";

  const nameError =
    internal || customerName.trim().length >= 2 ? null : "Müşteri adı zorunludur.";
  const requestedByError =
    !internal || requestedBy.trim().length >= 2
      ? null
      : `${reasonLabel} siparişinde "siparişi oluşturan yetkili" alanı zorunludur.`;
  const reasonNoteError =
    !internal || reasonNote.trim().length >= 3
      ? null
      : `${reasonLabel} siparişinde "sipariş sebebi / özel not" alanı zorunludur — ` +
        "sipariş niçin açıldığı yazılmadan kaydedilmez.";

  const linesError = lines.length === 0 ? "En az bir ürün ekleyin." : null;
  const lineErrors = lines.map((l) => {
    if (l.variantError) return "Renk listesi okunamadı. \"Yeniden dene\"ye basın.";
    if (l.variants === null) return "Renk seçenekleri yükleniyor…";
    if (l.variants.length > 0 && !l.variantId) return "Renk seçin.";
    const qty = Math.trunc(Number(l.quantity));
    if (!Number.isFinite(qty) || qty < 1) return "Adet en az 1 olmalı.";
    const unit = parseKurus(l.price);
    // 0 kuruş MEŞRUDUR: bedelsiz numune/çekim siparişi.
    if (unit === null || unit < 0) return "Birim fiyat geçersiz.";
    return null;
  });

  const canSave =
    !nameError &&
    !requestedByError &&
    !reasonNoteError &&
    !linesError &&
    lineErrors.every((e) => e === null) &&
    !create.pending;

  /* ── Kalem işlemleri ──────────────────────────────────────────────────── */
  /** Ürünün renk/seçenek listesini okur; sonuç ilgili kaleme yazılır. */
  function loadVariants(key: string, productId: string) {
    patchLine(key, { variants: null, variantError: null });
    callable<{ productId: string }, { variants: VariantOption[] }>("getProductVariants")({ productId })
      .then((res) => patchLine(key, { variants: res.variants ?? [], variantError: null }))
      .catch((e) => patchLine(key, { variants: [], variantError: callableErrorMessage(e) }));
  }

  function addLine(p: PickedProduct | null) {
    if (!p) return;
    const key = `${p.id}-${Date.now()}-${lines.length}`;
    setLines((prev) => [
      ...prev,
      {
        key,
        variants: null,
        variantId: "",
        variantError: null,
        product: p,
        quantity: "1",
        // ★ KATALOG FİYATI OTOMATİK GELİR. Ürünün fiyatı yoksa (0) kutu boş
        //   bırakılır — "0,00" yazmak, gerçekten bedelsiz bir sipariş ile fiyatı
        //   okunamamış bir kalemi ayırt edilemez hâle getirirdi.
        price: p.priceKurus > 0 ? formatKurusInput(p.priceKurus) : "",
        catalogPriceKurus: p.priceKurus,
      },
    ]);
    // Kalem listeye girdikten SONRA okunur; yanıt o kaleme yazılır.
    loadVariants(key, p.id);
  }

  function patchLine(key: string, patch: Partial<Line>) {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }

  function removeLine(key: string) {
    setLines((prev) => prev.filter((l) => l.key !== key));
  }

  /* ── Kaydet ───────────────────────────────────────────────────────────── */
  async function save() {
    setShowErrors(true);
    if (!canSave) return;

    /**
     * İç siparişte alıcı alanları GÖNDERİLMEZ (boş gider); sunucu onları
     * `settings/company`den yeniden yazar. Buradan şirket adını yollamamızın
     * tek sebebi şemanın `customerName` alanını dolu istemesidir — otorite
     * yine sunucudur, bu değer orada üzerine yazılır.
     */
    const alici = internal
      ? {
          customerName: companyName,
          phone: "",
          email: "",
          address: { address1: "", address2: "", city: "", province: "", zip: "" },
        }
      : {
          customerName: customerName.trim(),
          phone: phone.trim(),
          email: email.trim(),
          address: {
            address1: address1.trim(),
            address2: "",
            city: city.trim(),
            province: province.trim(),
            zip: zip.trim(),
          },
        };

    const out = await create.run({
      ...alici,
      items: lines.map((l) => ({
        productId: l.product.id,
        variantId: l.variantId,
        quantity: Math.trunc(Number(l.quantity)),
        // Para KURUŞ ve TAM SAYI gider; alan adı birimi söyler (`unitPriceKurus`).
        unitPriceKurus: parseKurus(l.price) ?? 0,
      })),
      cargoCompany: cargoCompany.trim(),
      note: note.trim(),
      reason,
      reasonNote: reasonNote.trim(),
      requestedBy: requestedBy.trim(),
      countsAsRevenue,
    });

    // `run()` hata durumunda null döner (fırlatmaz); mesaj `create.error`dadır.
    if (out) navigate(`/orders/${encodeURIComponent(out.orderId)}`);
  }

  /* ── Yetki ────────────────────────────────────────────────────────────── */
  if (!can.isStaff) {
    return (
      <PageHeader title="Manuel Sipariş">
        <NoAccess what="Manuel sipariş girişi" />
      </PageHeader>
    );
  }

  return (
    <PageHeader
      title="Manuel Sipariş"
      breadcrumb={[{ label: "Siparişler", to: "/orders" }, { label: "Manuel Sipariş" }]}
      description="Shopify'dan gelmeyen sipariş: fotoğraf çekimi, numune, iç kullanım. Kayıt yalnızca Balder'de tutulur, Shopify'a gönderilmez."
    >
      {create.error ? <ErrorNote error={create.error} className="mb-4" /> : null}

      {/*
        ★ FORM ARTIK DAR BİR SÜTUNA SIKIŞMIYOR.
        Eskiden `lg:grid-cols-3` + `col-span-2` idi: özet paneli ekranın üçte
        birini alıyor, form kalan üçte ikiye sıkışıyordu. Özet birkaç satır
        rakamdan ibaret; sabit 20rem yeter. Form kalan genişliğin TAMAMINI alır.
      */}
      <div className="grid grid-cols-1 gap-8 xl:grid-cols-[minmax(0,1fr)_20rem]">
        {/* ══ SOL: form ══════════════════════════════════════════════════ */}
        <div className="divide-y divide-gray-200">
          {/* ── 1. Sebep ──────────────────────────────────────────────── */}
          <section className="py-6 first:pt-0">
            <div className="mb-3 flex items-baseline gap-2">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-800">
                1
              </span>
              <h2 className="text-base font-semibold text-gray-900">Sipariş sebebi</h2>
            </div>

            {/*
              Dokunma hedefi: her seçenek en az 44px yüksekliğinde bir etiket.
              Seçenekler iki sütuna sığdırıldı — dört seçenek alt alta uzayınca
              altındaki alanların sebebe göre değiştiği fark edilmiyordu.
            */}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {REASONS.map((r) => (
                <label
                  key={r.id}
                  className={`flex min-h-[44px] cursor-pointer items-start gap-3 rounded-md border px-3 py-2 text-sm ${
                    reason === r.id
                      ? "border-primary-500 bg-primary-50 text-primary-900"
                      : "border-gray-300 bg-white text-gray-700"
                  }`}
                >
                  <input
                    type="radio"
                    name="manual-reason"
                    className="mt-0.5 h-5 w-5 shrink-0"
                    checked={reason === r.id}
                    onChange={() => setReason(r.id)}
                  />
                  <span className="min-w-0">
                    <span
                      className={`block ${reason === r.id ? "font-semibold" : "font-medium"}`}
                    >
                      {r.label}
                    </span>
                    <span className="block text-xs text-gray-600">{r.hint}</span>
                  </span>
                </label>
              ))}
            </div>

            {/*
              ★ SEÇİMİN SONUCU AÇIKÇA YAZAR. Eski ekranda alanlar sessizce
              görünüp kayboluyordu ve kullanıcı "müşteri bilgisi niye gitti"
              diye soruyordu. Bir cümlelik açıklama bunu ortadan kaldırır.
            */}
            <p
              className={`mt-3 rounded-md p-3 text-sm ${
                internal ? "bg-sky-50 text-sky-900" : "bg-gray-100 text-gray-700"
              }`}
            >
              {internal ? (
                <>
                  İç sipariş: alıcı <strong>{companyName}</strong>. Müşteri bilgisi
                  sorulmaz, şirket bilgilerinden otomatik doldurulur. Bunun yerine{" "}
                  <strong>siparişi açan yetkili</strong> ve <strong>sebep notu</strong>{" "}
                  zorunludur.
                </>
              ) : (
                <>
                  Gerçek bir alıcıya gidiyor: <strong>müşteri ve adres bilgileri</strong>{" "}
                  sorulur.
                </>
              )}
            </p>
          </section>

          {/* ── 2a. İÇ SİPARİŞ: kim, niçin ────────────────────────────────
              Yalnızca çekim/numune/iç kullanımda çizilir. */}
          {internal ? (
            <section className="py-6 first:pt-0">
              <div className="mb-1 flex items-baseline gap-2">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-800">
                  2
                </span>
                <h2 className="text-base font-semibold text-gray-900">Kim, niçin açıyor?</h2>
              </div>
              <p className="mb-3 text-sm text-gray-600">
                Bu sipariş aylar sonra raporda göründüğünde cevabı aranan iki soru.
                İkisi de zorunludur.
              </p>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field
                  label="Siparişi oluşturan yetkili"
                  required
                  error={showErrors ? requestedByError : null}
                  hint="Oturum sahibinin adı gelir; başkası adına açıyorsanız değiştirin."
                >
                  <input
                    className="input min-h-[44px]"
                    value={requestedBy}
                    onChange={(e) => setRequestedBy(e.target.value)}
                    maxLength={200}
                    autoComplete="off"
                  />
                </Field>
                <Field
                  label="Sipariş sebebi / özel not"
                  required
                  error={showErrors ? reasonNoteError : null}
                  hint="Ör. Instagram çekimi, Ankara bayi numunesi, muhasebe odası."
                >
                  <input
                    className="input min-h-[44px]"
                    value={reasonNote}
                    onChange={(e) => setReasonNote(e.target.value)}
                    maxLength={300}
                    autoComplete="off"
                  />
                </Field>
              </div>

              {/* ── Alıcı özeti — SALT OKUNUR ──────────────────────────────
                  Bilgi gizlenmiyor, yalnızca sorulmuyor: kullanıcı siparişin
                  hangi bilgilerle açılacağını görebilmeli. */}
              <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Alıcı — şirket bilgilerinden
                </p>
                {companyLoading ? (
                  <div className="mt-2">
                    <Spinner size="sm" label="Şirket bilgileri okunuyor" />
                  </div>
                ) : companyError ? (
                  <p className="mt-1 text-sm text-red-700">{companyError}</p>
                ) : companyMissing ? (
                  <p className="mt-1 text-sm text-amber-800">
                    Şirket bilgileri henüz tanımlanmamış. Sipariş "{companyName}" adına
                    açılır, adres boş kalır. Ayarlar → <strong>Şirket Bilgileri</strong>{" "}
                    bölümünden doldurun.
                  </p>
                ) : (
                  <dl className="mt-1 space-y-0.5 text-sm text-gray-800">
                    <div>
                      <dt className="sr-only">Ad</dt>
                      <dd className="font-medium">{companyName}</dd>
                    </div>
                    {company?.address1 ? <dd>{company.address1}</dd> : null}
                    {company?.city || company?.province ? (
                      <dd>
                        {[company?.city, company?.province, company?.zip]
                          .filter(Boolean)
                          .join(" / ")}
                      </dd>
                    ) : null}
                    {company?.phone ? <dd>{company.phone}</dd> : null}
                    {company?.email ? <dd>{company.email}</dd> : null}
                    {/* ★ "Adres alanları boş" uyarısı KALDIRILDI (kullanıcı kararı).
                        İç siparişte alıcı Demontha'nın kendisi; kargo etiketi
                        basılmıyor, adres kimseye gitmiyor. Her ekranda çıkan ve
                        yapılacak bir iş göstermeyen uyarı, gerçek uyarıları
                        görünmez kılar. Adres gerekiyorsa zaten Ayarlar → Şirket
                        Bilgileri'nden girilir. */}
                  </dl>
                )}
              </div>
            </section>
          ) : (
            /* ── 2b. DİĞER: alıcı bilgileri ──────────────────────────────
               "Diğer" sebebinde davranış eskisiyle BİREBİR aynıdır. */
            <section className="py-6 first:pt-0">
              <div className="mb-3 flex items-baseline gap-2">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-800">
                  2
                </span>
                <h2 className="text-base font-semibold text-gray-900">Müşteri / alıcı</h2>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Ad soyad" required error={showErrors ? nameError : null}>
                  <input
                    className="input min-h-[44px]"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    maxLength={200}
                    autoComplete="off"
                  />
                </Field>
                <Field label="Telefon">
                  <input
                    className="input min-h-[44px]"
                    inputMode="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    maxLength={40}
                    autoComplete="off"
                  />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="E-posta">
                    <input
                      className="input min-h-[44px]"
                      inputMode="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      maxLength={200}
                      autoComplete="off"
                    />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Adres">
                    <input
                      className="input min-h-[44px]"
                      value={address1}
                      onChange={(e) => setAddress1(e.target.value)}
                      maxLength={400}
                      autoComplete="off"
                    />
                  </Field>
                </div>
                {/* Üç dar alan yan yana: il/ilçe/posta kodu tam genişlik kutular
                    olarak alt alta dizildiğinde ekran gereksiz uzuyordu. */}
                <div className="grid grid-cols-2 gap-4 sm:col-span-2 sm:grid-cols-3">
                  <Field label="İlçe">
                    <input
                      className="input min-h-[44px]"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      maxLength={120}
                      autoComplete="off"
                    />
                  </Field>
                  <Field label="İl">
                    <input
                      className="input min-h-[44px]"
                      value={province}
                      onChange={(e) => setProvince(e.target.value)}
                      maxLength={120}
                      autoComplete="off"
                    />
                  </Field>
                  <Field label="Posta kodu">
                    <input
                      className="input min-h-[44px]"
                      inputMode="numeric"
                      value={zip}
                      onChange={(e) => setZip(e.target.value)}
                      maxLength={20}
                      autoComplete="off"
                    />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field
                    label="Sebep açıklaması"
                    hint="İsteğe bağlı. Ör. telefonla alınan sipariş, iade telafisi."
                  >
                    <input
                      className="input min-h-[44px]"
                      value={reasonNote}
                      onChange={(e) => setReasonNote(e.target.value)}
                      maxLength={300}
                      autoComplete="off"
                    />
                  </Field>
                </div>
              </div>
            </section>
          )}

          {/* ── 3. Kalemler ───────────────────────────────────────────── */}
          <section className="py-6 first:pt-0">
            <div className="mb-1 flex items-baseline gap-2">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-800">
                3
              </span>
              <h2 className="text-base font-semibold text-gray-900">Ürünler</h2>
            </div>
            <p className="mb-3 text-sm text-gray-600">
              Ürün katalogdan seçilir; satış fiyatı otomatik gelir ve elle
              değiştirilebilir.
            </p>

            {/*
              TEK seçici kullanılır ve seçim yapılır yapılmaz listeye eklenip
              sıfırlanır. Her kalem için ayrı seçici çizmek aynı DOM `id`'sini
              tekrarlardı — etiket tıklaması yanlış kutuyu odaklardı.
            */}
            <ProductPicker value={null} onChange={addLine} label="Ürün ekle" required={false} />

            {showErrors && linesError ? (
              <p role="alert" className="mt-2 text-sm text-red-700">
                {linesError}
              </p>
            ) : null}

            {lines.length > 0 ? (
              <ul className="mt-4 space-y-3">
                {lines.map((l, i) => {
                  const unit = parseKurus(l.price);
                  // Katalog fiyatından SAPMA rozeti: sessizce farklı bir fiyatla
                  // sipariş açmak, aylar sonra "niye bu tutar" sorusunu doğurur.
                  const changed =
                    l.catalogPriceKurus > 0 && unit !== null && unit !== l.catalogPriceKurus;
                  return (
                    <li key={l.key} className="rounded-md border border-gray-200 bg-gray-50 p-3">
                      <div className="flex items-start gap-3">
                        {l.product.imageUrl ? (
                          <img
                            src={l.product.imageUrl}
                            alt=""
                            className="h-12 w-12 shrink-0 rounded object-cover"
                          />
                        ) : (
                          <div className="h-12 w-12 shrink-0 rounded bg-gray-200" />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-gray-900">
                            {l.product.name}
                          </p>
                          <p className="truncate text-xs text-gray-600">
                            {l.product.sku ? `SKU ${l.product.sku}` : l.product.id}
                            {l.catalogPriceKurus > 0
                              ? ` · katalog ${tl(l.catalogPriceKurus)}`
                              : " · katalogda fiyat yok"}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeLine(l.key)}
                          aria-label={`${l.product.name} kalemini çıkar`}
                          className="btn-secondary shrink-0 !min-h-[44px] !min-w-[44px] lg:!min-h-[44px]"
                        >
                          Çıkar
                        </button>
                      </div>

                      {l.variantError ? (
                        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-red-700">
                          <span role="alert">Renk listesi okunamadı: {l.variantError}</span>
                          <button
                            type="button"
                            className="btn-secondary !min-h-[44px]"
                            onClick={() => loadVariants(l.key, l.product.id)}
                          >
                            Yeniden dene
                          </button>
                        </div>
                      ) : l.variants === null ? (
                        <p className="mt-3 text-sm text-gray-600">Renk seçenekleri yükleniyor…</p>
                      ) : l.variants.length > 0 ? (
                        <div className="mt-3">
                          <Field label="Renk / seçenek" required>
                            <select
                              className="input min-h-[44px]"
                              value={l.variantId}
                              onChange={(e) => patchLine(l.key, { variantId: e.target.value })}
                            >
                              <option value="">Renk seçin…</option>
                              {l.variants.map((v) => (
                                <option key={v.id} value={v.id}>
                                  {v.title}
                                  {v.sku ? ` (${v.sku})` : ""}
                                </option>
                              ))}
                            </select>
                          </Field>
                        </div>
                      ) : null}

                      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                        <Field label="Adet">
                          <input
                            className="input min-h-[44px] text-right tabular-nums"
                            inputMode="numeric"
                            value={l.quantity}
                            onChange={(e) =>
                              patchLine(l.key, { quantity: e.target.value.replace(/[^\d]/g, "") })
                            }
                          />
                        </Field>
                        <div className="sm:col-span-2">
                          <Field label="Birim fiyat (KDV dahil)">
                            <div className="relative">
                              <input
                                className="input min-h-[44px] pr-8 text-right tabular-nums"
                                inputMode="decimal"
                                lang="tr"
                                placeholder="0,00"
                                value={l.price}
                                onChange={(e) =>
                                  patchLine(l.key, {
                                    price: sanitizeDecimalKeystrokes(e.target.value),
                                  })
                                }
                                onBlur={() => {
                                  const k = parseKurus(l.price);
                                  if (k !== null) patchLine(l.key, { price: formatKurusInput(k) });
                                }}
                              />
                              <span
                                aria-hidden="true"
                                className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-sm text-gray-500"
                              >
                                ₺
                              </span>
                            </div>
                          </Field>
                        </div>
                        <div className="min-w-0">
                          <span className="label">Satır toplamı</span>
                          <p className="mt-1 flex min-h-[44px] items-center justify-end text-sm font-semibold tabular-nums text-gray-900">
                            {tl(lineTotalKurus(l))}
                          </p>
                        </div>
                      </div>

                      {changed ? (
                        <p className="mt-1 text-xs text-amber-800">
                          Fiyat katalogdan farklı ({tl(l.catalogPriceKurus)} →{" "}
                          {tl(unit ?? 0)}). Bilerek yapıldıysa sorun yok.
                        </p>
                      ) : null}

                      {showErrors && lineErrors[i] ? (
                        <p role="alert" className="mt-1 text-sm text-red-700">
                          {lineErrors[i]}
                        </p>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </section>

          {/* ── 4. Gönderim ve not ────────────────────────────────────── */}
          <section className="py-6 first:pt-0">
            <div className="mb-3 flex items-baseline gap-2">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-800">
                4
              </span>
              <h2 className="text-base font-semibold text-gray-900">Gönderim ve not</h2>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field
                label="Kargo firması"
                hint={internal ? "İç siparişte çoğu zaman gerekmez." : undefined}
              >
                <select
                  className="select min-h-[44px]"
                  value={cargoCompany}
                  onChange={(e) => setCargoCompany(e.target.value)}
                >
                  <option value="">Seçilmedi</option>
                  {CARGO_PROVIDERS.map((c) => (
                    <option key={c.label} value={c.label}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </Field>
              <div className="sm:col-span-2">
                <Field label="Sipariş notu" hint="Atölyeye/ofise iletilecek serbest not.">
                  <textarea
                    className="input"
                    rows={3}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    maxLength={2000}
                  />
                </Field>
              </div>
            </div>
          </section>
        </div>

        {/* ══ SAĞ: özet ══════════════════════════════════════════════════ */}
        <div>
          {/* Masaüstünde kaydırırken görünür kalır; tablet/telefonda akışta. */}
          <div className="space-y-4 xl:sticky xl:top-4">
            <section className="card card-pad">
              <h2 className="mb-3 text-base font-semibold text-gray-900">Toplam</h2>

              <dl className="space-y-2 text-sm">
                {/*
                  Fiyatlar KDV DAHİL girildiği için ara toplam, toplamın içinden
                  ayrıştırılan NET tutardır. Etiketler bunu açıkça söyler; aksi
                  hâlde "ara toplam + KDV = genel toplam" satırı kullanıcıya
                  KDV'nin üstüne eklendiğini düşündürürdü.
                */}
                <div className="flex items-baseline justify-between gap-3">
                  <dt className="text-gray-600">Kalem</dt>
                  <dd className="font-medium tabular-nums text-gray-900">{lines.length}</dd>
                </div>
                <div className="flex items-baseline justify-between gap-3">
                  <dt className="text-gray-600">Ara toplam (KDV hariç)</dt>
                  <dd className="font-medium tabular-nums text-gray-900">
                    {totals.tax === null ? "—" : tl(totals.net)}
                  </dd>
                </div>
                <div className="flex items-baseline justify-between gap-3">
                  <dt className="text-gray-600">
                    KDV{kdvRate === null ? "" : ` (%${kdvRate})`}
                  </dt>
                  <dd className="font-medium tabular-nums text-gray-900">
                    {totals.tax === null ? "—" : tl(totals.tax)}
                  </dd>
                </div>
                <div className="flex items-baseline justify-between gap-3 border-t border-gray-200 pt-2">
                  <dt className="font-semibold text-gray-900">Genel toplam</dt>
                  <dd className="text-lg font-bold tabular-nums text-gray-900">
                    {tl(totals.total)}
                  </dd>
                </div>
              </dl>

              {financeError ? (
                <p className="mt-3 text-xs text-gray-600">
                  KDV oranı okunamadı, kırılım gösterilemiyor. Genel toplam
                  etkilenmez — KDV fiyatın içinden ayrışır.
                </p>
              ) : null}

              <p className="mt-3 text-xs text-gray-500">
                Fiyatlar KDV dahil girilir. Kesin tutar kayıt sırasında sunucuda
                yeniden hesaplanır.
              </p>
            </section>

            {/* ── CİRO KARARI ────────────────────────────────────────── */}
            <section className="card card-pad">
              <h2 className="mb-2 text-base font-semibold text-gray-900">Ciro</h2>

              {/*
                ★ KARAR EKRANDA AÇIKÇA YAZAR. Çekim/numune gerçek satış değildir;
                ciroya sayılırsa aylık ciro ve kârlılık raporu, hiç para girmemiş
                siparişlerle şişer. Varsayılan "sayılmasın"dır; ofis gerçek bir
                satışı elle giriyorsa (ör. telefonla sipariş) bunu AÇIKÇA
                işaretler. Karar veriye yazılır, tahmin edilmez.
              */}
              <label className="flex min-h-[44px] cursor-pointer items-start gap-3 rounded-md border border-gray-300 bg-white p-3">
                <input
                  type="checkbox"
                  className="mt-0.5 h-5 w-5 shrink-0"
                  checked={countsAsRevenue}
                  onChange={(e) => setCountsAsRevenue(e.target.checked)}
                />
                <span className="text-sm text-gray-800">
                  Bu gerçek bir satış — ciroya sayılsın
                </span>
              </label>

              <div
                className={`mt-3 rounded-md p-3 text-sm ${
                  countsAsRevenue
                    ? "bg-amber-50 text-amber-900"
                    : "bg-gray-100 text-gray-700"
                }`}
              >
                {countsAsRevenue ? (
                  <>
                    Bu sipariş <strong>ciroya SAYILACAK</strong> ve kârlılık
                    raporunda görünecek. Sebep: {reasonLabel}.
                  </>
                ) : (
                  <>
                    Bu sipariş <strong>ciroya SAYILMAYACAK</strong>. Sebep:{" "}
                    {reasonLabel}. Üretim ve kesim listesi normal işler; yalnızca
                    ciro ve kârlılık raporunun dışında kalır.
                  </>
                )}
              </div>
            </section>

            <section className="card card-pad">
              <button
                type="button"
                className="btn-primary w-full"
                disabled={create.pending}
                onClick={() => void save()}
              >
                {create.pending ? "Kaydediliyor…" : "Siparişi Oluştur"}
              </button>
              <button
                type="button"
                className="btn-secondary mt-2 w-full"
                disabled={create.pending}
                onClick={() => navigate("/orders")}
              >
                Vazgeç
              </button>

              {/* Eksikler kaydetmeden ÖNCE de görünür: "Oluştur"a basıp hiçbir
                  şey olmaması, sessiz başarısızlığın en sinir bozucu hâli. */}
              {showErrors && !canSave ? (
                <ul className="mt-3 space-y-1 text-sm text-red-700">
                  {[nameError, requestedByError, reasonNoteError, linesError]
                    .filter(Boolean)
                    .map((m) => (
                      <li key={m as string}>{m}</li>
                    ))}
                </ul>
              ) : null}

              <p className="mt-3 text-xs text-gray-500">
                Sipariş <strong>Onaylandı</strong> statüsünde açılır ve Siparişler →
                Onaylandı sekmesinde görünür. Shopify'a gönderilmez, üretime
                otomatik düşmez.
              </p>
            </section>
          </div>
        </div>
      </div>
    </PageHeader>
  );
}
